/**
 * ThemeColorPanel - Color picker panel with categorized CSS variables
 * Adapted from shadcn-studio
 */

import { useState, useEffect, useCallback, useRef, InputHTMLAttributes } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

import { TailwindColorSelectorPopover } from "./tailwind-color-selector-popover";

type DebouncedInputProps = {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">;

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  // Track if user has interacted - only call onChange for user-initiated changes
  const userInteracted = useRef(false);

  // Sync value from parent, but only if user hasn't changed it
  useEffect(() => {
    if (!userInteracted.current) {
      setValue(initialValue);
    }
  }, [initialValue]);

  // Only call onChange when user has actually picked a color
  useEffect(() => {
    if (!userInteracted.current) {
      return;
    }

    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <input
      {...props}
      type="color"
      value={value}
      onChange={(e) => {
        userInteracted.current = true;
        setValue(e.target.value);
      }}
    />
  );
}

interface ColorSwatchProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export function ColorSwatch({ label, value, onChange }: ColorSwatchProps) {
  const [localValue, setLocalValue] = useState(value);

  // Always sync with parent value when it changes (e.g., theme switch)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Simple hex display - for OKLCH values, show as-is
  const displayValue = localValue.startsWith("#")
    ? localValue
    : localValue.length > 20
      ? localValue.substring(0, 20) + "..."
      : localValue;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground/70 font-mono text-[10px]">
          {displayValue}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <div
          className="relative flex size-8 cursor-pointer items-center justify-center overflow-hidden rounded-md"
          style={{ backgroundColor: localValue }}
        >
          <DebouncedInput
            id={`color-${label.replace(/\s+/g, "-").toLowerCase()}`}
            value={localValue.startsWith("#") ? localValue : "#888888"}
            onChange={(newValue) => {
              setLocalValue(newValue);
              onChange(newValue);
            }}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
        <TailwindColorSelectorPopover
          value={localValue}
          onSelect={(color) => {
            setLocalValue(color);
            onChange(color);
          }}
        />
        <input
          type="text"
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
            onChange(e.target.value);
          }}
          className={cn(
            "flex-1 rounded-md px-2.5 py-1.5 text-xs",
            "bg-muted text-foreground border border-border/50",
            "placeholder:text-muted-foreground",
            "focus:outline-none focus:ring-1 focus:ring-ring"
          )}
        />
      </div>
    </div>
  );
}

export interface ThemeStyleProps {
  [key: string]: string;
}

interface ThemeColorPanelProps {
  currentTheme: Partial<ThemeStyleProps> | undefined;
  onColorChange: (key: string, value: string) => void;
}

export function ThemeColorPanel({
  currentTheme,
  onColorChange,
}: ThemeColorPanelProps) {
  const updateColor = useCallback(
    (key: string, value: string) => {
      onColorChange(key, value);
    },
    [onColorChange]
  );

  // Create a stable key prefix from current theme values to force remount on theme change
  const themeKey = JSON.stringify(currentTheme);

  return (
    <div className="space-y-2">
      <Accordion
        type="multiple"
        defaultValue={["brand"]}
        className="w-full space-y-1"
      >
        {/* Brand Colors */}
        <AccordionItem value="brand" className="rounded-lg bg-muted/50 border border-border/50 px-3 border-0">
          <AccordionTrigger className="cursor-pointer py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline">
            Brand Colors
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-1 pb-3">
            <ColorSwatch
              key={`${themeKey}-primary`}
              label="Primary"
              value={currentTheme?.primary || ""}
              onChange={(value) => updateColor("primary", value)}
            />
            <ColorSwatch
              key={`${themeKey}-primary-foreground`}
              label="Primary Foreground"
              value={currentTheme?.["primary-foreground"] || ""}
              onChange={(value) => updateColor("primary-foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-secondary`}
              label="Secondary"
              value={currentTheme?.secondary || ""}
              onChange={(value) => updateColor("secondary", value)}
            />
            <ColorSwatch
              key={`${themeKey}-secondary-foreground`}
              label="Secondary Foreground"
              value={currentTheme?.["secondary-foreground"] || ""}
              onChange={(value) => updateColor("secondary-foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-destructive`}
              label="Destructive"
              value={currentTheme?.destructive || ""}
              onChange={(value) => updateColor("destructive", value)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Base Colors */}
        <AccordionItem value="base" className="rounded-lg bg-muted/50 border border-border/50 px-3 border-0">
          <AccordionTrigger className="cursor-pointer py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline">
            Base Colors
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-1 pb-3">
            <ColorSwatch
              key={`${themeKey}-background`}
              label="Background"
              value={currentTheme?.background || ""}
              onChange={(value) => updateColor("background", value)}
            />
            <ColorSwatch
              key={`${themeKey}-foreground`}
              label="Foreground"
              value={currentTheme?.foreground || ""}
              onChange={(value) => updateColor("foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-card`}
              label="Card"
              value={currentTheme?.card || ""}
              onChange={(value) => updateColor("card", value)}
            />
            <ColorSwatch
              key={`${themeKey}-card-foreground`}
              label="Card Foreground"
              value={currentTheme?.["card-foreground"] || ""}
              onChange={(value) => updateColor("card-foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-popover`}
              label="Popover"
              value={currentTheme?.popover || ""}
              onChange={(value) => updateColor("popover", value)}
            />
            <ColorSwatch
              key={`${themeKey}-popover-foreground`}
              label="Popover Foreground"
              value={currentTheme?.["popover-foreground"] || ""}
              onChange={(value) => updateColor("popover-foreground", value)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Other Colors */}
        <AccordionItem value="other" className="rounded-lg bg-muted/50 border border-border/50 px-3 border-0">
          <AccordionTrigger className="cursor-pointer py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline">
            Other Colors
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-1 pb-3">
            <ColorSwatch
              key={`${themeKey}-muted`}
              label="Muted"
              value={currentTheme?.muted || ""}
              onChange={(value) => updateColor("muted", value)}
            />
            <ColorSwatch
              key={`${themeKey}-muted-foreground`}
              label="Muted Foreground"
              value={currentTheme?.["muted-foreground"] || ""}
              onChange={(value) => updateColor("muted-foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-accent`}
              label="Accent"
              value={currentTheme?.accent || ""}
              onChange={(value) => updateColor("accent", value)}
            />
            <ColorSwatch
              key={`${themeKey}-accent-foreground`}
              label="Accent Foreground"
              value={currentTheme?.["accent-foreground"] || ""}
              onChange={(value) => updateColor("accent-foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-border`}
              label="Border"
              value={currentTheme?.border || ""}
              onChange={(value) => updateColor("border", value)}
            />
            <ColorSwatch
              key={`${themeKey}-input`}
              label="Input"
              value={currentTheme?.input || ""}
              onChange={(value) => updateColor("input", value)}
            />
            <ColorSwatch
              key={`${themeKey}-ring`}
              label="Ring"
              value={currentTheme?.ring || ""}
              onChange={(value) => updateColor("ring", value)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Sidebar Colors */}
        <AccordionItem value="sidebar" className="rounded-lg bg-muted/50 border border-border/50 px-3 border-0">
          <AccordionTrigger className="cursor-pointer py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline">
            Sidebar Colors
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-1 pb-3">
            <ColorSwatch
              key={`${themeKey}-sidebar`}
              label="Sidebar"
              value={currentTheme?.sidebar || ""}
              onChange={(value) => updateColor("sidebar", value)}
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-foreground`}
              label="Sidebar Foreground"
              value={currentTheme?.["sidebar-foreground"] || ""}
              onChange={(value) => updateColor("sidebar-foreground", value)}
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-primary`}
              label="Sidebar Primary"
              value={currentTheme?.["sidebar-primary"] || ""}
              onChange={(value) => updateColor("sidebar-primary", value)}
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-primary-foreground`}
              label="Sidebar Primary Foreground"
              value={currentTheme?.["sidebar-primary-foreground"] || ""}
              onChange={(value) =>
                updateColor("sidebar-primary-foreground", value)
              }
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-accent`}
              label="Sidebar Accent"
              value={currentTheme?.["sidebar-accent"] || ""}
              onChange={(value) => updateColor("sidebar-accent", value)}
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-accent-foreground`}
              label="Sidebar Accent Foreground"
              value={currentTheme?.["sidebar-accent-foreground"] || ""}
              onChange={(value) =>
                updateColor("sidebar-accent-foreground", value)
              }
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-border`}
              label="Sidebar Border"
              value={currentTheme?.["sidebar-border"] || ""}
              onChange={(value) => updateColor("sidebar-border", value)}
            />
            <ColorSwatch
              key={`${themeKey}-sidebar-ring`}
              label="Sidebar Ring"
              value={currentTheme?.["sidebar-ring"] || ""}
              onChange={(value) => updateColor("sidebar-ring", value)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Chart Colors */}
        <AccordionItem value="chart" className="rounded-lg bg-muted/50 border border-border/50 px-3 border-0">
          <AccordionTrigger className="cursor-pointer py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline">
            Chart Colors
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-1 pb-3">
            <ColorSwatch
              key={`${themeKey}-chart-1`}
              label="Chart 1"
              value={currentTheme?.["chart-1"] || ""}
              onChange={(value) => updateColor("chart-1", value)}
            />
            <ColorSwatch
              key={`${themeKey}-chart-2`}
              label="Chart 2"
              value={currentTheme?.["chart-2"] || ""}
              onChange={(value) => updateColor("chart-2", value)}
            />
            <ColorSwatch
              key={`${themeKey}-chart-3`}
              label="Chart 3"
              value={currentTheme?.["chart-3"] || ""}
              onChange={(value) => updateColor("chart-3", value)}
            />
            <ColorSwatch
              key={`${themeKey}-chart-4`}
              label="Chart 4"
              value={currentTheme?.["chart-4"] || ""}
              onChange={(value) => updateColor("chart-4", value)}
            />
            <ColorSwatch
              key={`${themeKey}-chart-5`}
              label="Chart 5"
              value={currentTheme?.["chart-5"] || ""}
              onChange={(value) => updateColor("chart-5", value)}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
